import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogType?: string;
  ogImage?: string;
  schema?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://alphasolutions237.com';

const replaceDomainInSchema = (obj: unknown): unknown => {
  if (typeof obj === 'string') {
    return obj.replace(/https:\/\/alphasolutions237\.com/g, SITE_URL);
  }
  if (Array.isArray(obj)) {
    return obj.map(replaceDomainInSchema);
  }
  if (obj !== null && typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = replaceDomainInSchema(value);
    }
    return result;
  }
  return obj;
};

export default function SEO({
  title,
  description,
  keywords,
  canonicalPath = '',
  ogType = 'website',
  ogImage,
  schema,
  noindex = false,
}: SEOProps) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    const tagsToClean: (HTMLElement | HTMLLinkElement | HTMLScriptElement)[] = [];
    const tagId = `seo-${Math.random().toString(36).slice(2, 9)}`;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const selector = isProperty
        ? `meta[property="${name}"]`
        : `meta[name="${name}"]`;
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        if (isProperty) el.setAttribute('property', name);
        else el.setAttribute('name', name);
        document.head.appendChild(el);
        tagsToClean.push(el);
      }
      el.setAttribute('content', content);
      el.setAttribute('data-seo-id', tagId);
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
        tagsToClean.push(el);
      }
      el.setAttribute('href', href);
      el.setAttribute('data-seo-id', tagId);
    };

    // Core meta
    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    setMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow');

    // Canonical
    const canonicalUrl = canonicalPath
      ? `${SITE_URL}${canonicalPath}`
      : `${SITE_URL}${window.location.pathname}`;
    setLink('canonical', canonicalUrl);

    // Open Graph
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalUrl, true);
    if (ogImage) setMeta('og:image', ogImage, true);

    // Twitter
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    if (ogImage) setMeta('twitter:image', ogImage);

    // Schema.org JSON-LD
    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      schemas.forEach((s) => {
        const processedSchema = replaceDomainInSchema(s) as Record<string, unknown>;
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(processedSchema);
        script.setAttribute('data-seo-id', tagId);
        script.setAttribute('data-seo-schema', 'true');
        document.head.appendChild(script);
        tagsToClean.push(script);
      });
    }

    return () => {
      document.title = previousTitle;
      tagsToClean.forEach((el) => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
    };
  }, [title, description, keywords, canonicalPath, ogType, ogImage, schema, noindex]);

  return null;
}